import { Locale } from '~/types/locale';
import { Plan } from '~/types/plan';

export interface ExtendedPlan extends Plan {
  inclusionsTitle?: string;
  inclusionsDescription?: string;
  inclusions?: string[];
  recommended: boolean;
  recommendedText?: string;
}

export type Plans = Record<Locale, ExtendedPlan[]>;

export const plans: Plans = {
  au: [
    {
      experiment: false,
      title: 'Free',
      slug: 'free',
      code: 'free',
      subheading: 'Try before you buy.',
      footer: 'No credit card required',
      usageAllowance: 3,
      price: {
        value: 0,
        code: 'AUD',
        symbol: '$',
      },
      recommended: false,
      inclusions: ['Non-Editable Resources', 'Downloads Limited to Free Resource Collection'],
    },
    {
      experiment: false,
      title: 'Essential',
      subheading: 'Print and go.',
      slug: 'essential_annual',
      code: 'essential_annual',
      usageAllowance: -1,
      price: {
        value: 104,
        displayAsMonthly: true,
        code: 'AUD',
        symbol: '$',
      },
      recommended: false,
      period: 'annually',
      footer: '$104 billed yearly',
      inclusions: ['Non-Editable Resources', 'All Printable PDF Downloads Available (No Limits)'],
    },
    {
      experiment: false,
      title: 'Plus',
      subheading: 'Edit and engage.',
      code: 'teach_plus',
      slug: 'plus_annual',
      footer: '$157 billed yearly',
      usageAllowance: -1,
      price: {
        value: 157,
        displayAsMonthly: true,
        code: 'AUD',
        symbol: '$',
      },
      recommended: true,
      recommendedText: 'Most Popular',
      period: 'annually',
      inclusions: [
        'Editable and Non-Editable Resources',
        'All File Formats — Google Slides, PowerPoint, PDF, etc.',
        'Unit Plans',
        'Lesson Plans',
        'Digital Games',
        'Classroom Videos',
        'Advanced Tools',
        'And more...',
      ],
    },
    {
      experiment: true,
      title: 'School Packages',
      subheading: 'SAVE 20%',
      subheadingPrice: '$157',
      footer: 'Per teacher, per year',
      slug: 'school_plan',
      code: 'school_plan',
      usageAllowance: -1,
      price: {
        value: 157,
        discountPercentage: 20,
        displayAsMonthly: false,
        code: 'AUD',
        symbol: '$',
      },
      recommended: false,
      period: 'annually',
      hideFromDetailsTable: true,
    },
  ],
  us: [
    {
      title: 'Free',
      code: 'free',
      slug: 'free',
      usageAllowance: 1,
      price: {
        value: 0,
        code: 'USD',
        symbol: '$',
      },
      recommended: false,
      subheading: 'Try before you buy.',
      footer: 'No credit card required',
      inclusions: ['Non-Editable Resources', 'Downloads Limited to Free Resource Collection'],
    },
    {
      title: 'Plus',
      code: 'plus_monthly',
      slug: 'plus_monthly',
      usageAllowance: -1,
      price: {
        value: 120,
        code: 'USD',
        symbol: '$',
        displayAsMonthly: true,
      },
      recommended: false,
      period: 'monthly',
      subheading: 'Edit and engage.',
      inclusions: [
        'Editable and Non-Editable Resources',
        'All File Formats — Google Slides, PowerPoint, PDF, etc.',
        'Unit Plans',
        'Lesson Plans',
        'Digital Games',
        'Classroom Videos',
        'Advanced Tools',
        'And more...',
      ],
    },
    {
      title: 'Lite',
      code: 'plus_lite',
      slug: 'plus_lite',
      usageAllowance: 3,
      price: {
        value: 45,
        code: 'USD',
        symbol: '$',
        displayAsMonthly: true,
      },
      recommended: false,
      period: 'annually',
      subheading: '3 Downloads Per Month.',
      inclusions: [
        'Access to our entire premium resource library with 3 downloads per month',
        'Estimated price per resource approximately $1.25',
        'Editable and Non-Editable Resources',
        'All File Formats - Google Slides, PowerPoint, PDF, etc',
      ],
      hideFromDetailsTable: false,
    },
    {
      title: 'Plus',
      subheading: 'Edit and engage.',
      code: 'teach_plus',
      slug: 'plus_annual',
      usageAllowance: -1,
      price: {
        value: 73,
        undiscountedPrice: 120,
        code: 'USD',
        symbol: '$',
        displayAsMonthly: true,
      },
      recommended: true,
      period: 'annually',
      inclusions: [
        'Editable and Non-Editable Resources',
        'All File Formats — Google Slides, PowerPoint, PDF, etc.',
        'Unit Plans',
        'Lesson Plans',
        'Digital Games',
        'Classroom Videos',
        'Advanced Tools',
        'And more...',
      ],
    },
  ],
  gb: [
    {
      title: 'Free',
      code: 'free',
      slug: 'free',
      usageAllowance: 1,
      price: {
        value: 0,
        code: 'GBP',
        symbol: '£',
      },
      recommended: false,
      subheading: 'Free includes:',
      inclusions: ['Non-Editable Resources', 'Downloads Limited to Free Resource Collection'],
    },
    {
      title: 'Essential',
      code: 'essential_annual',
      slug: 'essential_annual',
      usageAllowance: -1,
      price: {
        value: 41,
        code: 'GBP',
        symbol: '£',
        displayAsMonthly: true,
      },
      recommended: false,
      period: 'annually',
      subheading: 'Print and go.',
      inclusions: ['Non-Editable Resources', 'All Printable PDF Downloads Available (No Limits)'],
    },
    {
      title: 'Plus',
      code: 'teach_plus',
      slug: 'plus_annual',
      usageAllowance: -1,
      price: {
        value: 62,
        code: 'GBP',
        symbol: '£',
        displayAsMonthly: true,
      },
      recommended: true,
      recommendedText: 'Most Popular',
      period: 'annually',
      subheading: 'Edit and engage.',
      inclusions: [
        'Editable and Non-Editable Resources',
        'All File Formats — Google Slides, PowerPoint, PDF, etc.',
        'Unit Plans',
        'Lesson Plans',
        'Digital Games',
        'Classroom Videos',
        'Advanced Tools',
        'And more...',
      ],
    },
  ],
};

export default plans;
